import './GiftCardInventoryV2.css';

import DataTable from 'react-data-table-component';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
    GET_GC_CARD_INVENTORIES_FOR_PROPERTY,
    GET_DISTINCT_REASONS_FOR_PROPERTY,
    CREATE_GC_CARD_INVENTORY,
    UPDATE_GC_CARD_INVENTORY_BALANCE,
    DELETE_GC_CARD_INVENTORY,
    CREATE_CARD_UPDATE,
    GET_LATEST_CARD_ON_HAND,
    GET_CARD_UPDATES_FOR_CARD,
    GET_PROMO_ITEMS_FOR_PROPERTY,
    CREATE_PROMO_ITEM,
    DELETE_PROMO_ITEM,
    CREATE_PROMO_UPDATE,
    GET_LATEST_PROMO_REMAINING,
    GET_PROMO_UPDATES_FOR_ITEM,
    GET_DISTINCT_PROMO_REASONS_FOR_PROPERTY,
    useLazyQuery,
    useMutation,
    client,
} from 'utils/graphql';
import { formatUTCDateTime } from 'utils/date.util';
import User from 'utils/user.util';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import InfoModal from 'components/Modals/InfoModal/InfoModal';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import CardDetailModal from 'views/GiftCardInventory/CardDetailModal';
import PromoItemModal from 'views/GiftCardInventory/PromoItemModal';

import { ReactComponent as CheckIcon } from 'assets/icons/check_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';

const TABS = { CARDS: 'cards', PROMO: 'promo' };

const formatDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');
const isExpired = (d) => !!d && d.slice(0, 10) < new Date().toISOString().slice(0, 10);

const GiftCardInventoryV2 = ({ property }) => {
    const { t } = useTranslation();
    const csvLinkRef = useRef(null);
    const promoCsvLinkRef = useRef(null);

    // Tab state
    const [activeTab, setActiveTab] = useState(TABS.CARDS);

    // ── Cards state ──
    const [cards, setCards] = useState([]);
    const [totalCardCount, setTotalCardCount] = useState(0);
    const [cardPage, setCardPage] = useState(1);
    const [cardRowsPerPage, setCardRowsPerPage] = useState(10);
    const [isLoadingCards, setIsLoadingCards] = useState(true);

    const [cardTypeFilter, setCardTypeFilter] = useState('');
    const [searchText, setSearchText] = useState('');
    const [existingReasons, setExistingReasons] = useState([]);

    // Card modal
    const [showCardModal, setShowCardModal] = useState(false);
    const [cardModalMode, setCardModalMode] = useState('create');
    const [selectedCard, setSelectedCard] = useState(null);
    const [isCardSubmitting, setIsCardSubmitting] = useState(false);
    const [savedUpdateId, setSavedUpdateId] = useState(null);

    // ── Promo state ──
    const [promoItems, setPromoItems] = useState([]);
    const [totalPromoCount, setTotalPromoCount] = useState(0);
    const [promoPage, setPromoPage] = useState(1);
    const [promoRowsPerPage, setPromoRowsPerPage] = useState(10);
    const [isLoadingPromo, setIsLoadingPromo] = useState(true);
    const [existingPromoReasons, setExistingPromoReasons] = useState([]);

    // Promo modal
    const [showPromoModal, setShowPromoModal] = useState(false);
    const [promoModalMode, setPromoModalMode] = useState('create');
    const [selectedPromo, setSelectedPromo] = useState(null);
    const [isPromoSubmitting, setIsPromoSubmitting] = useState(false);
    const [savedPromoUpdateId, setSavedPromoUpdateId] = useState(null);

    // Shared modals
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteType, setDeleteType] = useState(null); // 'card' | 'promo'
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [infoModalText, setInfoModalText] = useState('');
    const [infoModalOptions, setInfoModalOptions] = useState({});
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);

    // CSV
    const [csvFileName, setCsvFileName] = useState('');
    const [csvData, setCsvData] = useState([]);
    const [isLoadingCsv, setIsLoadingCsv] = useState(false);
    const [promoCsvFileName, setPromoCsvFileName] = useState('');
    const [promoCsvData, setPromoCsvData] = useState([]);
    const [isLoadingPromoCsv, setIsLoadingPromoCsv] = useState(false);

    // ── GraphQL hooks ──
    const [getCardsForProperty] = useLazyQuery(GET_GC_CARD_INVENTORIES_FOR_PROPERTY, {
        fetchPolicy: 'network-only',
    });
    const [getDistinctReasons] = useLazyQuery(GET_DISTINCT_REASONS_FOR_PROPERTY, {
        fetchPolicy: 'cache-and-network',
    });
    const [createCardMutation] = useMutation(CREATE_GC_CARD_INVENTORY);
    const [updateCardBalanceMutation] = useMutation(UPDATE_GC_CARD_INVENTORY_BALANCE);
    const [deleteCardMutation] = useMutation(DELETE_GC_CARD_INVENTORY);
    const [createCardUpdateMutation] = useMutation(CREATE_CARD_UPDATE);
    const [getLatestCardUpdate] = useLazyQuery(GET_CARD_UPDATES_FOR_CARD, {
        fetchPolicy: 'network-only',
    });

    const [getPromoItemsForProperty] = useLazyQuery(GET_PROMO_ITEMS_FOR_PROPERTY, {
        fetchPolicy: 'network-only',
    });
    const [createPromoMutation] = useMutation(CREATE_PROMO_ITEM);
    const [deletePromoMutation] = useMutation(DELETE_PROMO_ITEM);
    const [createPromoUpdateMutation] = useMutation(CREATE_PROMO_UPDATE);
    const [getLatestPromoUpdate] = useLazyQuery(GET_PROMO_UPDATES_FOR_ITEM, {
        fetchPolicy: 'network-only',
    });
    const [getDistinctPromoReasons] = useLazyQuery(GET_DISTINCT_PROMO_REASONS_FOR_PROPERTY, {
        fetchPolicy: 'cache-and-network',
    });

    // ── Build filter for cards query ──
    const buildCardFilter = () => {
        const filters = [
            { left: 'property__c_id', right: property.id, expression: '=' },
            // Distinguish V2 records: they have card_number or num_cards set
            // Or just use presence of status field as V2 marker
        ];
        if (cardTypeFilter !== '') {
            filters.push({ left: 'card_type', right: cardTypeFilter, expression: '=' });
        }
        if (searchText) {
            filters.push({
                left: 'card_number',
                right: `%${searchText}%`,
                expression: 'LIKE',
                or_: [
                    { left: 'serial_number', right: `%${searchText}%`, expression: 'LIKE' },
                    { left: 'card_type', right: `%${searchText}%`, expression: 'LIKE' },
                ],
            });
        }
        return filters;
    };

    // ── Load cards ──
    const loadCards = async () => {
        setIsLoadingCards(true);
        try {
            const { data } = await getCardsForProperty({
                variables: {
                    offset: (cardPage - 1) * cardRowsPerPage,
                    limit: cardRowsPerPage,
                    filter: buildCardFilter(),
                },
            });

            if (data?.table) {
                setTotalCardCount(data.table.count);
                const mappedCards = data.table.records.map((r) => {
                    const inv = r.gc_card_inventory;
                    const createdByName =
                        inv.created_by_propertyuser__c_list?.[0]?.propertyuser__c?.name ||
                        t('deleted_user');
                    return {
                        ...inv,
                        num_cards: inv.num_cards != null ? parseInt(inv.num_cards) : null,
                        initial_value:
                            inv.initial_value != null ? parseFloat(inv.initial_value) : null,
                        current_balance:
                            inv.current_balance != null ? parseFloat(inv.current_balance) : null,
                        created_by_name: createdByName,
                    };
                });
                // Fetch latest cards-on-hand for each card in parallel using client.query
                // so each call is fully independent (useLazyQuery shares state across calls)
                const onHandResults = await Promise.all(
                    mappedCards.map((card) =>
                        client
                            .query({
                                query: GET_LATEST_CARD_ON_HAND,
                                variables: { card_id: card.id },
                                fetchPolicy: 'no-cache',
                            })
                            .then(({ data: d }) => {
                                const row = d?.table?.records?.[0];
                                const nb =
                                    row?.gc_card_inventory_updates?.new_balance ??
                                    row?.new_balance ??
                                    null;
                                return {
                                    id: card.id,
                                    latest_on_hand: nb != null ? parseInt(nb) : null,
                                };
                            })
                            .catch(() => ({ id: card.id, latest_on_hand: null }))
                    )
                );
                const onHandMap = Object.fromEntries(
                    onHandResults.map((r) => [r.id, r.latest_on_hand])
                );

                // Merge enriched data and set state once to avoid intermediate render without latest_on_hand
                setCards((prev) => {
                    const prevLatestMap = Object.fromEntries(
                        prev.map((c) => [c.id, c.latest_on_hand ?? null])
                    );
                    return mappedCards.map((c) => ({
                        ...c,
                        latest_on_hand: onHandMap[c.id] ?? prevLatestMap[c.id] ?? null,
                    }));
                });
            }
        } catch (error) {
            console.error('Failed to load cards:', error);
        }
        setIsLoadingCards(false);
    };

    // ── Load distinct reasons for card dropdown ──
    const loadDistinctReasons = async () => {
        try {
            const { data } = await getDistinctReasons({
                variables: { property__c_id: property.id },
            });
            if (data?.table?.records) {
                const reasons = [
                    ...new Set(
                        data.table.records
                            .map((r) => r.gc_card_inventory?.reason_for_activation)
                            .filter(Boolean)
                    ),
                ];
                setExistingReasons(reasons);
            }
        } catch {
            // non-critical
        }
    };

    // ── Load promo items ──
    const loadPromoItems = async () => {
        setIsLoadingPromo(true);
        try {
            const { data } = await getPromoItemsForProperty({
                variables: {
                    property__c_id: property.id,
                    offset: (promoPage - 1) * promoRowsPerPage,
                    limit: promoRowsPerPage,
                },
            });
            if (data?.table) {
                setTotalPromoCount(data.table.count);
                const items = data.table.records.map((r) => {
                    const pi = r.gc_inventory_promo_items;
                    const createdByName =
                        pi.created_by_propertyuser__c_list?.[0]?.propertyuser__c?.name || '';
                    return {
                        ...pi,
                        quantity: pi.quantity != null ? parseInt(pi.quantity) : null,
                        remaining_inventory:
                            pi.remaining_inventory != null
                                ? parseInt(pi.remaining_inventory)
                                : null,
                        created_by_name: createdByName,
                    };
                });
                // Fetch latest remaining inventory for each item in parallel
                const remainingResults = await Promise.all(
                    items.map((item) =>
                        client
                            .query({
                                query: GET_LATEST_PROMO_REMAINING,
                                variables: { promo_item_id: item.id },
                                fetchPolicy: 'no-cache',
                            })
                            .then(({ data: d }) => {
                                const row = d?.table?.records?.[0];
                                const nb =
                                    row?.gc_inventory_promo_item_updates?.new_remaining_inventory ??
                                    row?.new_remaining_inventory ??
                                    null;
                                return {
                                    id: item.id,
                                    latest_remaining: nb != null ? parseInt(nb) : null,
                                };
                            })
                            .catch(() => ({ id: item.id, latest_remaining: null }))
                    )
                );
                const remainingMap = Object.fromEntries(
                    remainingResults.map((r) => [r.id, r.latest_remaining])
                );
                // Merge enriched data and set state once to avoid intermediate render without latest_remaining
                setPromoItems((prev) => {
                    const prevLatestMap = Object.fromEntries(
                        prev.map((p) => [p.id, p.latest_remaining ?? null])
                    );
                    return items.map((p) => ({
                        ...p,
                        latest_remaining: remainingMap[p.id] ?? prevLatestMap[p.id] ?? null,
                    }));
                });
            }
        } catch (error) {
            console.error('Failed to load promo items:', error);
        }
        setIsLoadingPromo(false);
    };

    // ── Load distinct reasons for promo datalist ──
    const loadDistinctPromoReasons = async () => {
        try {
            const { data } = await getDistinctPromoReasons({
                variables: { property__c_id: property.id },
            });
            if (data?.table?.records) {
                const reasons = [
                    ...new Set(
                        data.table.records
                            .map((r) => r.gc_inventory_promo_items?.reason)
                            .filter(Boolean)
                    ),
                ];
                setExistingPromoReasons(reasons);
            }
        } catch {
            // non-critical
        }
    };

    // ── Card CRUD handlers ──
    const handleCardSubmit = async (formData) => {
        setIsCardSubmitting(true);
        try {
            if (formData._isUpdate) {
                // New update log from detail view
                const updatedDate = formatUTCDateTime();
                const { data: createData, errors: createErrors } = await createCardUpdateMutation({
                    variables: {
                        card_id: selectedCard.id,
                        property__c_id: property.id,
                        new_balance: formData.new_balance,
                        num_distributed: formData.num_distributed,
                        num_destroyed: formData.num_destroyed,
                        num_transferred: formData.num_transferred,
                        num_lost: formData.num_lost,
                        given_to: formData.given_to,
                        notes: formData.notes,
                        updated_by: User.getUser()?.id,
                        updated_date: updatedDate,
                    },
                });
                if (createErrors || !createData?.createRow?.success) throw new Error();
                // Update current_balance on the card: cards_on_hand * face_value
                if (formData.new_balance != null && selectedCard?.initial_value != null) {
                    const newBalance =
                        parseFloat(formData.new_balance) * parseFloat(selectedCard.initial_value);
                    await updateCardBalanceMutation({
                        variables: { id: selectedCard.id, current_balance: newBalance },
                    });
                }
                // Optimistically update the cards list right away
                if (formData.new_balance != null) {
                    setCards((prev) =>
                        prev.map((c) =>
                            c.id === selectedCard.id
                                ? { ...c, latest_on_hand: parseInt(formData.new_balance) }
                                : c
                        )
                    );
                }
                // Fetch the new update entry's id so attachments can be linked to it
                const { data: latestData } = await getLatestCardUpdate({
                    variables: { card_id: selectedCard.id },
                });
                const newUpdateId =
                    latestData?.table?.records?.[0]?.gc_card_inventory_updates?.id || null;
                setSavedUpdateId(newUpdateId);
                // Keep modal open for attachment uploads
            } else {
                // Create new card
                const { data, errors } = await createCardMutation({
                    variables: {
                        ...formData,
                        created_by: User.getUser()?.id,
                        property__c_id: property.id,
                        date: formatUTCDateTime(),
                    },
                });
                if (errors || !data?.createRow?.success) throw new Error();
                setShowCardModal(false);
                showInfoModal('giftcard_create_success', true);
                loadCards();
                loadDistinctReasons();
            }
        } catch {
            showInfoModal(
                formData._isUpdate ? 'giftcard_update_fail' : 'giftcard_create_fail',
                false
            );
        }
        setIsCardSubmitting(false);
    };

    const handleDeleteCard = async () => {
        try {
            const { data, errors } = await deleteCardMutation({
                variables: { id: deleteTarget.id },
            });
            if (errors || !data?.deleteRow?.success) throw new Error();
            showInfoModal('giftcard_delete_success', true);
            loadCards();
        } catch {
            showInfoModal('giftcard_delete_fail', false);
        }
        setDeleteTarget(null);
    };

    // ── Promo CRUD handlers ──
    const handlePromoSubmit = async (formData) => {
        setIsPromoSubmitting(true);
        try {
            if (formData._isUpdate) {
                // New update log from detail view
                const updatedDate = formatUTCDateTime();
                const { data: createData, errors: createErrors } = await createPromoUpdateMutation({
                    variables: {
                        promo_item_id: selectedPromo.id,
                        property__c_id: property.id,
                        new_remaining_inventory: formData.new_remaining_inventory,
                        remaining_use: formData.remaining_use,
                        notes: formData.notes,
                        updated_by: User.getUser()?.id,
                        updated_date: updatedDate,
                    },
                });
                if (createErrors || !createData?.createRow?.success) throw new Error();
                // Immediately update the promo items list with the new remaining value
                if (formData.new_remaining_inventory != null) {
                    setPromoItems((prev) =>
                        prev.map((p) =>
                            p.id === selectedPromo.id
                                ? {
                                      ...p,
                                      latest_remaining: parseInt(formData.new_remaining_inventory),
                                  }
                                : p
                        )
                    );
                }
                // Fetch the new update entry's id so attachments can be linked to it
                const { data: latestData } = await getLatestPromoUpdate({
                    variables: { promo_item_id: selectedPromo.id },
                });
                const newUpdateId =
                    latestData?.table?.records?.[0]?.gc_inventory_promo_item_updates?.id || null;
                setSavedPromoUpdateId(newUpdateId);
                // Keep modal open for attachment uploads — list is updated via immediate patch above
            } else {
                // Create new promo item
                const { data, errors } = await createPromoMutation({
                    variables: {
                        ...formData,
                        created_by: User.getUser()?.id,
                        property__c_id: property.id,
                        date: formatUTCDateTime(),
                    },
                });
                if (errors || !data?.createRow?.success) throw new Error();
                setShowPromoModal(false);
                showInfoModal('promo_create_success', true);
                loadPromoItems();
            }
        } catch {
            showInfoModal(formData._isUpdate ? 'promo_update_fail' : 'promo_create_fail', false);
        }
        setIsPromoSubmitting(false);
    };

    const handleDeletePromo = async () => {
        try {
            const { data, errors } = await deletePromoMutation({
                variables: { id: deleteTarget.id },
            });
            if (errors || !data?.deleteRow?.success) throw new Error();
            showInfoModal('promo_delete_success', true);
            loadPromoItems();
        } catch {
            showInfoModal('promo_delete_fail', false);
        }
        setDeleteTarget(null);
    };

    const confirmDelete = () => {
        if (deleteType === 'card') handleDeleteCard();
        else handleDeletePromo();
    };

    // ── Info modal helper ──
    const showInfoModal = (textKey, success) => {
        setInfoModalText(textKey);
        setInfoModalOptions({
            header: success ? (
                <CheckIcon fill="var(--bs-green)" />
            ) : (
                <ErrorIcon fill="var(--bs-red)" />
            ),
            center: true,
        });
        setInfoModalIsOpen(true);
    };

    // ── CSV ──
    const cardCsvHeaders = [
        { label: t('card_number'), key: 'card_number' },
        { label: t('serial_number'), key: 'serial_number' },
        { label: t('card_type'), key: 'card_type' },
        { label: t('status'), key: 'status' },
        { label: t('cards_on_hand'), key: 'cards_on_hand' },
        { label: t('initial_value'), key: 'initial_value' },
        { label: t('current_balance'), key: 'current_balance' },
        { label: t('activation_date'), key: 'activation_date' },
        { label: t('expiry_date'), key: 'expiry_date' },
        { label: t('reason_for_activation'), key: 'reason_for_activation' },
        { label: t('notes'), key: 'notes' },
    ];

    const loadCardCsvData = async () => {
        setCsvFileName(
            `gc_card_inventory_${property.name}_${formatUTCDateTime()}.csv`.replaceAll(' ', '_')
        );
        setIsLoadingCsv(true);
        try {
            const { data } = await getCardsForProperty({
                variables: {
                    offset: 0,
                    limit: 2000,
                    filter: buildCardFilter(),
                },
            });
            if (data?.table) {
                const mapped = data.table.records.map((r) => r.gc_card_inventory);
                const chunkSize = 25;
                const onHandResults = [];

                for (let i = 0; i < mapped.length; i += chunkSize) {
                    const chunk = mapped.slice(i, i + chunkSize);
                    const chunkResults = await Promise.all(
                        chunk.map((card) =>
                            client
                                .query({
                                    query: GET_LATEST_CARD_ON_HAND,
                                    variables: { card_id: card.id },
                                    fetchPolicy: 'no-cache',
                                })
                                .then(({ data: d }) => {
                                    const row = d?.table?.records?.[0];
                                    const nb =
                                        row?.gc_card_inventory_updates?.new_balance ??
                                        row?.new_balance ??
                                        null;
                                    return {
                                        id: card.id,
                                        latest_on_hand: nb != null ? parseInt(nb) : null,
                                    };
                                })
                                .catch(() => ({ id: card.id, latest_on_hand: null }))
                        )
                    );
                    onHandResults.push(...chunkResults);
                }

                const onHandMap = Object.fromEntries(
                    onHandResults.map((r) => [r.id, r.latest_on_hand])
                );
                setCsvData(
                    mapped.map((card) => {
                        const onHand =
                            onHandMap[card.id] ??
                            (card.num_cards != null ? parseInt(card.num_cards) : null);
                        const initialValue =
                            card.initial_value != null ? parseFloat(card.initial_value) : null;
                        const expired = isExpired(card.expiry_date);
                        return {
                            ...card,
                            status: expired ? t('card_expired') : t('active'),
                            cards_on_hand: onHand ?? '',
                            current_balance:
                                onHand != null && initialValue != null
                                    ? (onHand * initialValue).toFixed(2)
                                    : '',
                        };
                    })
                );
            }
        } catch {
            showInfoModal('csv_file_fail', false);
        }
        setIsLoadingCsv(false);
    };

    useEffect(() => {
        if (!csvData || csvData.length === 0) return;

        const csvLink = csvLinkRef.current?.link;
        if (csvLink) csvLink.click();
    }, [csvData]);

    const promoCsvHeaders = [
        { label: t('description'), key: 'description' },
        { label: t('brand'), key: 'brand' },
        { label: t('quantity'), key: 'quantity' },
        { label: t('remaining_inventory'), key: 'remaining_inventory' },
        { label: t('event_date'), key: 'event_date' },
        { label: t('distribution_method'), key: 'distribution_method' },
        { label: t('reason'), key: 'reason' },
        { label: t('notes'), key: 'notes' },
    ];

    const loadPromoCsvData = async () => {
        setPromoCsvFileName(
            `gc_promo_items_${property.name}_${formatUTCDateTime()}.csv`.replaceAll(' ', '_')
        );
        setIsLoadingPromoCsv(true);
        try {
            const { data } = await getPromoItemsForProperty({
                variables: {
                    property__c_id: property.id,
                    offset: 0,
                    limit: 2000,
                },
            });
            if (data?.table) {
                const mapped = data.table.records.map((r) => r.gc_inventory_promo_items);
                const promoRemainingBatchSize = 25;
                const remainingResults = [];

                for (let i = 0; i < mapped.length; i += promoRemainingBatchSize) {
                    const batch = mapped.slice(i, i + promoRemainingBatchSize);
                    const batchResults = await Promise.all(
                        batch.map((item) =>
                            client
                                .query({
                                    query: GET_LATEST_PROMO_REMAINING,
                                    variables: { promo_item_id: item.id },
                                    fetchPolicy: 'no-cache',
                                })
                                .then(({ data: d }) => {
                                    const row = d?.table?.records?.[0];
                                    const nb =
                                        row?.gc_inventory_promo_item_updates
                                            ?.new_remaining_inventory ??
                                        row?.new_remaining_inventory ??
                                        null;
                                    return {
                                        id: item.id,
                                        latest_remaining: nb != null ? parseInt(nb) : null,
                                    };
                                })
                                .catch(() => ({ id: item.id, latest_remaining: null }))
                        )
                    );

                    remainingResults.push(...batchResults);
                }

                const remainingMap = Object.fromEntries(
                    remainingResults.map((r) => [r.id, r.latest_remaining])
                );
                setPromoCsvData(
                    mapped.map((item) => {
                        const remaining =
                            remainingMap[item.id] ??
                            (item.remaining_inventory != null
                                ? parseInt(item.remaining_inventory)
                                : null) ??
                            (item.quantity != null ? parseInt(item.quantity) : null);
                        return {
                            ...item,
                            remaining_inventory: remaining ?? '',
                        };
                    })
                );
            }
        } catch {
            showInfoModal('csv_file_fail', false);
        }
        setIsLoadingPromoCsv(false);
    };

    useEffect(() => {
        const promoCsvLink = promoCsvLinkRef.current?.link;
        if (promoCsvData && promoCsvData.length > 0 && promoCsvLink) promoCsvLink.click();
    }, [promoCsvData]);

    // ── Card columns ──
    const cardColumns = [
        {
            name: t('card_type'),
            sortable: false,
            grow: 1,
            selector: (row) => row.card_type || '—',
        },
        {
            name: t('status'),
            sortable: false,
            grow: 1,
            cell: (row) => {
                const expired = isExpired(row.expiry_date);
                return (
                    <span className={`status-badge ${expired ? 'expired' : 'active'}`}>
                        {expired ? t('card_expired') : t('active')}
                    </span>
                );
            },
        },
        {
            name: t('cards_on_hand'),
            sortable: false,
            grow: 1,
            selector: (row) => {
                if (row.latest_on_hand != null) return row.latest_on_hand;
                return row.num_cards != null ? row.num_cards : '—';
            },
        },
        {
            name: t('value_per_card'),
            sortable: false,
            grow: 1,
            selector: (row) =>
                row.initial_value != null ? `$${parseFloat(row.initial_value).toFixed(2)}` : '—',
        },
        {
            name: t('total_balance'),
            sortable: false,
            grow: 1,
            selector: (row) => {
                const total = (row.num_cards || 0) * (row.initial_value || 0);
                return `$${total.toFixed(2)}`;
            },
        },
        {
            name: t('current_balance'),
            sortable: false,
            grow: 1,
            selector: (row) => {
                const onHand = row.latest_on_hand != null ? row.latest_on_hand : row.num_cards;
                if (onHand != null && row.initial_value != null)
                    return `$${(onHand * row.initial_value).toFixed(2)}`;
                return '—';
            },
        },
        {
            name: t('reason'),
            sortable: false,
            grow: 1,
            cell: (row) => (
                <span
                    title={row.reason_for_activation || ''}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        maxWidth: '100%',
                    }}
                >
                    {row.reason_for_activation || '—'}
                </span>
            ),
        },
        {
            name: '',
            sortable: false,
            ignoreRowClick: true,
            width: '60px',
            cell: (row) => (
                <button
                    className="btn btn-link p-1"
                    title={t('delete')}
                    onClick={() => {
                        setDeleteTarget(row);
                        setDeleteType('card');
                        setIsDeleteConfirmOpen(true);
                    }}
                >
                    <DeleteIcon
                        fill="var(--bs-danger)"
                        width={20}
                        height={20}
                        style={{ marginBottom: '7px' }}
                    />
                </button>
            ),
        },
    ];

    // ── Promo columns ──
    const promoColumns = [
        {
            name: t('description'),
            sortable: false,
            selector: (row) => row.description || '—',
            minWidth: '200px',
        },
        {
            name: t('brand'),
            sortable: false,
            selector: (row) => row.brand || '—',
        },
        {
            name: t('quantity'),
            sortable: false,
            maxWidth: '90px',
            selector: (row) => (row.quantity != null ? row.quantity : '—'),
        },
        {
            name: t('remaining_inventory'),
            sortable: false,
            maxWidth: '130px',
            selector: (row) =>
                row.latest_remaining != null
                    ? row.latest_remaining
                    : row.remaining_inventory != null
                      ? row.remaining_inventory
                      : row.quantity != null
                        ? row.quantity
                        : '—',
        },
        {
            name: t('event_date'),
            sortable: false,
            maxWidth: '110px',
            selector: (row) => formatDate(row.event_date),
        },
        {
            name: t('distribution_method'),
            sortable: false,
            selector: (row) => row.distribution_method || '—',
        },
        {
            name: t('reason'),
            sortable: false,
            selector: (row) => row.reason || '—',
        },
        {
            name: '',
            sortable: false,
            ignoreRowClick: true,
            width: '60px',
            cell: (row) => (
                <button
                    className="btn btn-link p-1"
                    title={t('delete')}
                    onClick={() => {
                        setDeleteTarget(row);
                        setDeleteType('promo');
                        setIsDeleteConfirmOpen(true);
                    }}
                >
                    <DeleteIcon
                        fill="var(--bs-danger)"
                        width={20}
                        height={20}
                        style={{ marginBottom: '7px' }}
                    />
                </button>
            ),
        },
    ];

    // ── Effects ──
    useEffect(() => {
        loadCards();
        loadDistinctReasons();
    }, [property.id, cardPage, cardRowsPerPage, cardTypeFilter]);

    useEffect(() => {
        loadPromoItems();
        loadDistinctPromoReasons();
    }, [property.id, promoPage, promoRowsPerPage]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (cardPage === 1) loadCards();
            else setCardPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchText, property.id]);

    return (
        <div className="gc-inventory-v2">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="m-0">{t('gift_card_inventory')}</h3>
                <div className="d-flex gap-2">
                    <Link to="/giftcardinventory/legacy" className="btn btn-outline-secondary">
                        {t('legacy_view')}
                    </Link>
                </div>
            </div>

            {/* Tab bar */}
            <div className="tab-bar d-flex mb-0">
                <Button
                    variant="link"
                    className={activeTab === TABS.CARDS ? 'active-tab' : ''}
                    onClick={() => setActiveTab(TABS.CARDS)}
                >
                    {t('cards')}
                </Button>
                <Button
                    variant="link"
                    className={activeTab === TABS.PROMO ? 'active-tab' : ''}
                    onClick={() => setActiveTab(TABS.PROMO)}
                >
                    {t('promotional_items')}
                </Button>
            </div>

            {/* ═══ Cards Tab ═══ */}
            {activeTab === TABS.CARDS && (
                <>
                    <div className="filter-row mt-3">
                        <Form.Select
                            value={cardTypeFilter}
                            onChange={(e) => {
                                setCardTypeFilter(e.target.value);
                                setCardPage(1);
                            }}
                        >
                            <option value="">{t('all_cards')}</option>
                            <option value="Promotion">{t('card_type_promotion')}</option>
                            <option value="Regular">{t('card_type_regular')}</option>
                        </Form.Select>
                        <Form.Control
                            type="text"
                            placeholder={t('search')}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                    <div className="d-flex gap-2 mb-3">
                        <Button
                            className="flex-grow-1"
                            onClick={() => {
                                setSelectedCard(null);
                                setCardModalMode('create');
                                setShowCardModal(true);
                            }}
                        >
                            {t('add_card')}
                        </Button>
                        <LoadingButton
                            onClick={loadCardCsvData}
                            isLoading={isLoadingCsv}
                            variant="secondary"
                        >
                            {t('download_csv')}
                        </LoadingButton>
                    </div>
                    <CSVLink
                        ref={csvLinkRef}
                        data={csvData}
                        headers={cardCsvHeaders}
                        filename={csvFileName}
                        style={{ display: 'none' }}
                    />
                    <DataTable
                        columns={cardColumns}
                        data={cards}
                        pagination
                        paginationServer
                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                        paginationTotalRows={totalCardCount}
                        onChangePage={(page) => setCardPage(page)}
                        onChangeRowsPerPage={(rpp) => setCardRowsPerPage(rpp)}
                        progressPending={isLoadingCards}
                        progressComponent={
                            <div className="d-flex justify-content-center align-items-center my-4">
                                <Spinner />
                            </div>
                        }
                        highlightOnHover
                        onRowClicked={(row) => {
                            setSelectedCard(row);
                            setCardModalMode('detail');
                            setSavedUpdateId(null);
                            setShowCardModal(true);
                        }}
                        persistTableHead
                        conditionalRowStyles={[
                            {
                                when: (row) => isExpired(row.expiry_date),
                                style: {
                                    backgroundColor: 'var(--bs-warning-bg-subtle)',
                                    color: 'var(--bs-warning-text-emphasis)',
                                },
                            },
                        ]}
                        customStyles={{
                            rows: { style: { cursor: 'pointer' } },
                        }}
                        paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
                    />
                </>
            )}

            {/* ═══ Promo Items Tab ═══ */}
            {activeTab === TABS.PROMO && (
                <>
                    <div className="d-flex gap-2 mb-3 mt-3">
                        <Button
                            className="flex-grow-1"
                            onClick={() => {
                                setSelectedPromo(null);
                                setPromoModalMode('create');
                                setShowPromoModal(true);
                            }}
                        >
                            {t('add_promo_item')}
                        </Button>
                        <LoadingButton
                            onClick={loadPromoCsvData}
                            isLoading={isLoadingPromoCsv}
                            variant="secondary"
                        >
                            {t('download_csv')}
                        </LoadingButton>
                    </div>
                    <CSVLink
                        ref={promoCsvLinkRef}
                        data={promoCsvData}
                        headers={promoCsvHeaders}
                        filename={promoCsvFileName}
                        style={{ display: 'none' }}
                    />
                    <DataTable
                        columns={promoColumns}
                        data={promoItems}
                        pagination
                        paginationServer
                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                        paginationTotalRows={totalPromoCount}
                        onChangePage={(page) => setPromoPage(page)}
                        onChangeRowsPerPage={(rpp) => setPromoRowsPerPage(rpp)}
                        progressPending={isLoadingPromo}
                        progressComponent={
                            <div className="d-flex justify-content-center align-items-center my-4">
                                <Spinner />
                            </div>
                        }
                        highlightOnHover
                        onRowClicked={(row) => {
                            setSelectedPromo(row);
                            setPromoModalMode('detail');
                            setSavedPromoUpdateId(null);
                            setShowPromoModal(true);
                        }}
                        persistTableHead
                        customStyles={{
                            rows: { style: { cursor: 'pointer' } },
                        }}
                        paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
                    />
                </>
            )}

            {/* ═══ Modals ═══ */}
            <CardDetailModal
                show={showCardModal}
                onHide={() => {
                    const wasDetail = cardModalMode === 'detail';
                    setShowCardModal(false);
                    setSavedUpdateId(null);
                    if (wasDetail) loadCards();
                }}
                mode={cardModalMode}
                card={selectedCard}
                onSubmit={handleCardSubmit}
                isSubmitting={isCardSubmitting}
                existingReasons={existingReasons}
                savedUpdateId={savedUpdateId}
            />
            <PromoItemModal
                show={showPromoModal}
                onHide={() => {
                    const wasDetail = promoModalMode === 'detail';
                    setShowPromoModal(false);
                    setSavedPromoUpdateId(null);
                    if (wasDetail) loadPromoItems();
                }}
                mode={promoModalMode}
                item={selectedPromo}
                onSubmit={handlePromoSubmit}
                isSubmitting={isPromoSubmitting}
                existingReasons={existingPromoReasons}
                savedUpdateId={savedPromoUpdateId}
            />
            <ConfirmationModal
                isOpen={isDeleteConfirmOpen}
                setOpen={setIsDeleteConfirmOpen}
                onConfirm={confirmDelete}
                options={{
                    center: true,
                    header: <WarningIcon fill="var(--bs-yellow)" height={48} width={48} />,
                }}
            >
                <p>{t('confirm_delete_entry')}</p>
            </ConfirmationModal>
            <InfoModal
                isOpen={infoModalIsOpen}
                setOpen={setInfoModalIsOpen}
                options={infoModalOptions}
            >
                {t(infoModalText)}
            </InfoModal>
        </div>
    );
};

export default GiftCardInventoryV2;
